import { GetTaxonomyResponse, UpdateTaxonomyRequest, UpdateTaxonomyResponse } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { GET_TENANT_TAXONOMY_QUERY_KEY } from './useGetTenantTaxonomy';

type CachedTaxonomy = AxiosResponse<GetTaxonomyResponse>;

export const useUpdateTaxonomy = (taxonomyId: string) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const queryKey = [GET_TENANT_TAXONOMY_QUERY_KEY, tenantId];

  const { mutate, isPending, error } = useMutation<
    AxiosResponse<UpdateTaxonomyResponse>,
    Error,
    UpdateTaxonomyRequest,
    { previous: CachedTaxonomy | undefined }
  >({
    mutationFn: data => api.taxonomy.updateTaxonomy(tenantId, taxonomyId, data),
    onMutate: async data => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CachedTaxonomy>(queryKey);
      if (previous?.data.taxonomy) {
        queryClient.setQueryData<CachedTaxonomy>(queryKey, {
          ...previous,
          data: {
            taxonomy: {
              ...previous.data.taxonomy,
              ...(data.name === undefined ? {} : { name: data.name }),
              ...(data.autoLabelOptedIn === undefined ? {} : { autoLabelOptedIn: data.autoLabelOptedIn }),
            },
          },
        });
      }
      return { previous };
    },
    onError: (_error, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: response => {
      queryClient.setQueryData(queryKey, {
        ...response,
        data: { taxonomy: response.data.taxonomy },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_TENANT_TAXONOMY_QUERY_KEY],
        exact: false,
      });
    },
  });

  return {
    updateTaxonomy: mutate,
    isUpdating: isPending,
    updateError: getApiError(error),
  };
};
